/**
 * A tenant's lease search (schema 1.3, `pipeline.kind: "lease-search"`) and a buyer's building search (BUY1,
 * `pipeline.kind: "buy-search"`) - the words and the scorecard's vocabulary. The two read the same scorecard; only the
 * words differ, and a buy search holds its street addresses back (the contract decides, the page never does).
 *
 * ⛔ Nothing here produces a figure or a claim anyone reads. A building's facts, its scorecard rows and its tally are
 * the contract's own strings and counts (the import gate re-counts the tally); this file only says which WORD and
 * which MARK a status is drawn with, and which words a tenant's page uses where a buyer's says "deal" and "cap".
 *
 * The contract types are MIRRORED here, not imported: a frontend file never imports backend source (it typechecks
 * locally and fails the Vercel build - 2026-09-09).
 */

export type PipelineKind = 'acquisition' | 'lease-search' | 'buy-search';

/** Absent (every contract before 1.3) is a buyer's pipeline; the server refuses a kind it does not know. */
export const pipelineKindOf = (pipeline: { kind?: unknown } | null | undefined): PipelineKind =>
  pipeline?.kind === 'lease-search' ? 'lease-search' : pipeline?.kind === 'buy-search' ? 'buy-search' : 'acquisition';

/** A spec search: the scorecard-and-cards pipeline (a tenant's lease search, a buyer's building search), not a deal pipeline. */
export const isSpecSearch = (kind: PipelineKind): boolean => kind === 'lease-search' || kind === 'buy-search';

/** The words that differ between the two pages. The buyer's are exactly what the page said before 1.3. */
export interface KindWords {
  criteria: string;
  deals: string;
  terms: string;
  termsNav: string;
  openItemOwner: string;
  viewDeal: string;
  /** The map popup's button and its asking line; absent, the popup keeps its own (a buyer's) words. */
  popup?: { goTo: string; ask: string };
  /** The short printed handout's sections and its hint; absent, the print chooser keeps its own (a buyer's). */
  print?: { shortKeys: string[]; termsHint: string };
  /**
   * How a scorecard's tally counts its UNKNOWN rows: "checking" on a buy search, whose unknown row itself reads "Checking"
   * (dock-high doors nobody has counted yet). Absent, the tally keeps "not stated" - a listing that simply does not say -
   * so the other two kinds' words are exactly what they were.
   */
  unknownTally?: string;
}

export const KIND_WORDS: Record<PipelineKind, KindWords> = {
  acquisition: {
    criteria: 'Your criteria',
    deals: 'Deals',
    terms: 'Deal terms',
    termsNav: 'Deal terms',
    openItemOwner: 'Owner',
    viewDeal: 'View this deal',
  },
  'lease-search': {
    criteria: 'Your requirement',
    deals: 'Buildings',
    terms: 'Buildings at a glance',
    termsNav: 'At a glance',
    // On a page of buildings an "owner" is a landlord; an open item says who it waits on.
    openItemOwner: 'Waiting on',
    viewDeal: 'View this building',
    popup: { goTo: 'Go to building', ask: 'Asking rent' },
    // What the page exists to put first - his requirement and the best matches - is on the short handout too.
    print: {
      shortKeys: ['overview', 'criteria', 'lead', 'openItems', 'terms'],
      termsHint: 'The at-a-glance table keeps every building on paper, one line each.',
    },
  },
  'buy-search': {
    criteria: 'Your search',
    deals: 'Buildings',
    terms: 'Buildings at a glance',
    termsNav: 'At a glance',
    openItemOwner: 'Waiting on',
    viewDeal: 'View this building',
    unknownTally: 'checking',
    // The short handout leads with the Buying tab's own sections; the at-a-glance table is not on it, so no hint.
    print: { shortKeys: ['buying', 'criteria', 'lead'], termsHint: '' },
  },
};

export type ScoreStatus = 'PASS' | 'NEAR' | 'FAIL' | 'UNKNOWN' | 'ABOVE';

/** The order a tally reads in - what meets first, what is simply not known after what is known. */
export const SCORE_ORDER: ScoreStatus[] = ['PASS', 'NEAR', 'FAIL', 'UNKNOWN', 'ABOVE'];

interface ScoreMeta {
  /** Drawn before the word; aria-hidden, because the word says it. Empty for UNKNOWN - it gets no cross and no tick. */
  mark: string;
  word: string;
  className: string;
  /** How n buildings' worth of this status reads in a tally. */
  tally: (n: number) => string;
}

/**
 * ⭐ UNKNOWN IS NOT FAIL. Most listings do not state a trailer count and most do not disclose a rent; drawn as a
 * failure, that would misstate the market. It has its own class, no mark, and reads "not stated". ABOVE is an
 * asking rent above the tenant's target - a comparison, not a miss - so it shares the near-miss's amber, not red.
 */
export const SCORE_META: Record<ScoreStatus, ScoreMeta> = {
  PASS: { mark: '✓', word: 'meets', className: 'portal-score-pass', tally: (n) => (n === 1 ? '1 meets' : `${n} meet`) },
  NEAR: { mark: '≈', word: 'close', className: 'portal-score-near', tally: (n) => `${n} close` },
  FAIL: { mark: '✗', word: 'does not meet', className: 'portal-score-fail', tally: (n) => (n === 1 ? '1 does not meet' : `${n} do not meet`) },
  UNKNOWN: { mark: '', word: 'not stated', className: 'portal-score-unknown', tally: (n) => `${n} not stated` },
  ABOVE: { mark: '↑', word: 'above your target', className: 'portal-score-above', tally: (n) => `${n} above your target` },
};

export const scoreMeta = (status: string | null | undefined): ScoreMeta | null =>
  status && Object.prototype.hasOwnProperty.call(SCORE_META, status) ? SCORE_META[status as ScoreStatus] : null;

/*
  The phone's at-a-glance list (TP7, 2026-10-13) draws a mark per row of his spec with no room for the words, so "not
  stated" needs a glyph there: a grey "?" - a question, never a cross. Every other mark IS the card's own, read off
  SCORE_META, so the two cannot drift. Everywhere else an unknown row keeps its words and no mark.
*/
const GLANCE_UNKNOWN = '?';

/** The mark a row's status gets on the phone's one-line list; null for a status the page does not know. */
export const glanceMark = (status: string | null | undefined): string | null => {
  const meta = scoreMeta(status);
  return meta ? meta.mark || GLANCE_UNKNOWN : null;
};

/** The phone list's key: every mark beside the word the cards use for it, in the order a tally reads. */
export const GLANCE_KEY = SCORE_ORDER.map((s) => `${SCORE_META[s].mark || GLANCE_UNKNOWN} ${SCORE_META[s].word}`).join(' · ');

/**
 * "4 meet · 1 not stated" from the contract's own counts. Empty when it carries none. `unknownWord` is the kind's word for
 * an unknown row (`KindWords.unknownTally`): a buy search's reads "2 meet · 1 checking".
 */
export const tallyText = (counts: Record<string, number> | null | undefined, unknownWord: string = 'not stated'): string =>
  SCORE_ORDER.filter((s) => typeof counts?.[s] === 'number' && (counts[s] as number) > 0)
    .map((s) => {
      const n = (counts as Record<string, number>)[s];
      return s === 'UNKNOWN' ? `${n} ${unknownWord}` : SCORE_META[s].tally(n);
    })
    .join(' · ');

export interface ScorecardRowDef {
  key: string;
  label?: string;
  threshold?: string | null;
}

export interface LeaseFloorRow {
  label?: string;
  threshold?: string | null;
  actual?: string | null;
  status?: string;
}

export interface LeaseScorecard {
  floors?: Record<string, LeaseFloorRow | null | undefined>;
  counts?: Record<string, number>;
}

export interface LeaseFact {
  label?: string;
  value?: string;
}

/** What the lease-search card and table read off a pipeline deal. */
export interface LeaseSearchDeal {
  id: string;
  name?: string;
  city?: string;
  verdict?: string | null;
  status?: { group?: string; label?: string; asOf?: string | null };
  facts?: LeaseFact[] | null;
  factsBasis?: string | null;
  terms?: { scorecard?: unknown } | null;
  /** Schema 1.4: the tour sheet's letter, and what Apex is still confirming. */
  mapLetter?: string | null;
  toConfirm?: string[] | null;
}

export const scorecardOf = (deal: LeaseSearchDeal): LeaseScorecard | null => {
  const sc = deal.terms?.scorecard;
  return sc !== null && typeof sc === 'object' ? (sc as LeaseScorecard) : null;
};
